import { useEffect, useState } from "react";
import { OwnerNav } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Phone, Mail, Search, Save } from "lucide-react";
import { toast, Toaster } from "sonner";

function frDate(iso) {
  return iso ? new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get("/owner/clients");
      if (!cancelled) setClients(data);
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = clients.filter((c) => {
    const s = q.toLowerCase();
    return !s || (c.name || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s) || (c.phone || "").includes(s);
  });

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
      <OwnerNav />
      <Toaster theme="dark" />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-2">Clients</div>
        <h1 className="font-serif text-5xl font-light mb-10">Fiches clients.</h1>

        <div className="relative max-w-md mb-8">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6760]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client…" className="pl-10 bg-[#141311] border-[#2C2A26]" data-testid="client-search" />
        </div>

        {filtered.length === 0 ? (
          <p className="text-[#6B6760]">Aucun client pour le moment.</p>
        ) : (
          <div className="border border-[#2C2A26]" data-testid="clients-table">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-[#141311] text-[10px] uppercase tracking-[0.2em] text-[#6B6760]">
              <div className="col-span-4">Client</div>
              <div className="col-span-3 hidden sm:block">Contact</div>
              <div className="col-span-1 text-center">Visites</div>
              <div className="col-span-2 text-center hidden sm:block">Dernière</div>
              <div className="col-span-2 text-right">Total</div>
            </div>
            {filtered.map((c) => (
              <button key={c.key} onClick={() => setSelected(c)} className="w-full grid grid-cols-12 gap-4 px-5 py-4 items-center border-t border-[#2C2A26] hover:bg-[#141311] text-left transition" data-testid={`client-row-${c.key}`}>
                <div className="col-span-4">
                  <div className="font-medium">{c.name || "—"}</div>
                  {c.note && <div className="text-[10px] text-[#C5A059] uppercase tracking-[0.15em] mt-1">★ Note</div>}
                  {c.upcoming > 0 && <div className="text-[10px] text-[#7CBF8E] mt-0.5">{c.upcoming} RDV à venir</div>}
                </div>
                <div className="col-span-3 hidden sm:flex flex-col text-xs text-[#A39E93]">
                  {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                  {c.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{c.email}</span>}
                </div>
                <div className="col-span-1 text-center font-serif text-2xl text-[#C5A059]">{c.visits}</div>
                <div className="col-span-2 text-center hidden sm:block text-sm text-[#A39E93]">{frDate(c.last_visit)}</div>
                <div className="col-span-2 text-right font-medium">{c.total_spent}€</div>
              </button>
            ))}
          </div>
        )}
      </div>
      {selected && <ClientDialog client={selected} onClose={() => setSelected(null)} onNoteSaved={(note) => setClients((cs) => cs.map((x) => (x.key === selected.key ? { ...x, note } : x)))} />}
    </div>
  );
}

function ClientDialog({ client, onClose, onNoteSaved }) {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get("/owner/client", { params: { key: client.key } });
      if (!cancelled) { setDetail(data); setNote(data.note || ""); }
    })();
    return () => { cancelled = true; };
  }, [client.key]);

  const saveNote = async () => {
    try {
      await api.put("/owner/client/note", { key: client.key, note });
      toast.success("Note enregistrée");
      onNoteSaved(note);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0A0908] border-[#2C2A26] text-[#F4F0EA] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="client-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl font-light">{client.name || "Client"}</DialogTitle></DialogHeader>
        <div className="text-sm text-[#A39E93] flex gap-4 flex-wrap">
          {client.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{client.phone}</span>}
          {client.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{client.email}</span>}
        </div>
        <div className="grid grid-cols-3 gap-3 my-4">
          <MiniStat label="Visites" value={client.visits} />
          <MiniStat label="À venir" value={client.upcoming} />
          <MiniStat label="Total dépensé" value={`${client.total_spent}€`} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A39E93] mb-2">Note privée</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full bg-[#141311] border border-[#2C2A26] p-3 text-sm" placeholder="Préférences, allergies, remarques…" data-testid="client-note" />
          <button onClick={saveNote} className="btn-primary mt-3" data-testid="client-note-save"><Save className="w-4 h-4" /> Enregistrer la note</button>
        </div>
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A39E93] mb-3">Historique</div>
          {!detail ? <p className="text-[#6B6760] text-sm">Chargement…</p> : detail.appointments.length === 0 ? <p className="text-[#6B6760] text-sm">Aucun rendez-vous.</p> : (
            <div className="divide-y divide-[#2C2A26] border-y border-[#2C2A26]">
              {detail.appointments.map((a) => (
                <div key={a.id} className={`flex items-center justify-between py-3 ${a.status === "cancelled" ? "opacity-40" : ""}`} data-testid={`client-appt-${a.id}`}>
                  <div>
                    <div className="text-sm">{frDate(a.date)} · {a.start_time}</div>
                    <div className="text-xs text-[#A39E93]">{a.service_name}{a.staff_name ? ` · ${a.staff_name}` : ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm">{a.price}€</div>
                    {a.status === "cancelled" && <div className="text-[9px] uppercase text-[#8C3131]">Annulé</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-[#141311] border border-[#2C2A26] p-4 text-center">
      <div className="font-serif text-3xl text-[#C5A059]">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6760] mt-1">{label}</div>
    </div>
  );
}
